import { html, render } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { Dialog } from '@vaadin/vaadin-core'
import { View } from '../view'
import { DeviceStore, DownloadResult } from '../../stores'

const closeIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`
const alertIcon = html`<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`
const installIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`

export const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

@customElement('kob-device-download')
export class DeviceDownload extends View {

    @property({ type: Object })
    deviceStore: DeviceStore

    result: DownloadResult

    @property()
    dialogOpened: boolean

    @property()
    installedOpened: boolean

    formRoot

    render() {
        return html`
            <vaadin-dialog
                aria-label="Successfully Installed"
                overlay-class="kob-modal"
                .opened="${this.installedOpened}"
                .renderer="${this.renderSuccess}"
            ></vaadin-dialog>
            <vaadin-dialog
                aria-label="Install Koblime"
                overlay-class="kob-modal"
                .opened="${this.dialogOpened}"
                @opened-changed="${(e: CustomEvent) => (this.dialogOpened = e.detail.value)}"
                .renderer="${this.renderContent}"
            ></vaadin-dialog>
        `
    }

    handleClose = () => {
        this.deviceStore = null
        this.installedOpened = false
        this.dialogOpened = false
    }

    handleDownload = async () => {
        this.result = null
        this.renderContent(this.formRoot)

        const result = await this.deviceStore.installKoblime()
        if (result.status !== 'error' && result.status !== 'cancel') {
            this.dialogOpened = false
            if (this.deviceStore.canDirectInstall)
                this.installedOpened = true
            return
        }

        this.result = result
        this.renderContent(this.formRoot)
    }

    header = (title: string) => html`
        <div class="modal-head">
            <h3 class="modal-title">${title}</h3>
            <button class="modal-close" aria-label="Close" @click="${this.handleClose}">${closeIcon}</button>
        </div>
    `

    renderSuccess = (root: HTMLElement, dialog?: Dialog) => {
        render(html`
            <div class="kob-dialog">
                ${this.header('All done')}
                <div class="modal-body">
                    <p>Koblime is on your device. Safely unplug the Kobo when you're ready.</p>
                    <p>It restarts once, and that's the install finished.</p>
                </div>
                <div class="modal-foot right">
                    <button class="btn btn-primary btn-sm" @click="${this.handleClose}">Done</button>
                </div>
            </div>
        `, root)
    }

    mobileTemplate = () => html`
        <div class="kob-dialog">
            ${this.header('Install Koblime')}
            <div class="modal-body">
                <div class="modal-banner danger">
                    ${alertIcon}
                    <div>Installing needs a desktop computer — your Kobo plugs into it with a cable.</div>
                </div>
            </div>
            <div class="modal-foot right">
                <button class="btn btn-ghost btn-sm" @click="${this.handleClose}">Cancel</button>
            </div>
        </div>
    `

    unsupportedTemplate = () => html`
        <div class="kob-dialog">
            ${this.header('Install Koblime')}
            <div class="modal-body">
                <div class="modal-banner danger">
                    ${alertIcon}
                    <div>This model's hardware can't run the Koblime sync app. Syncing won't work.</div>
                </div>
            </div>
            <div class="modal-foot right">
                <button class="btn btn-ghost btn-sm" @click="${this.handleClose}">Close</button>
            </div>
        </div>
    `

    renderContent = (root: HTMLElement, dialog?: Dialog) => {
        this.formRoot = root

        const template = this.deviceStore.isUnsupported ?
            this.unsupportedTemplate() :
            isMobile() ?
            this.mobileTemplate() :
            html`
                <div class="kob-dialog">
                    ${this.header('Install Koblime')}
                    <div class="modal-body">
                        <div class="modal-banner danger">
                            ${alertIcon}
                            <div>
                                This <code>KoboRoot.tgz</code> is unique to
                                <strong>${this.deviceStore.name}</strong>. Don't share it or put it on another device.
                            </div>
                        </div>

                        ${this.result ?
                            html`
                                <div class="modal-banner danger">
                                    ${alertIcon}
                                    <div>${this.result.message}</div>
                                </div>
                            ` : ''
                        }

                        ${this.deviceStore.canDirectInstall ?
                            html`
                                <p>Plug in your Kobo, then let Koblime write the file straight into the
                                <code>.kobo</code> folder. Safely unplug when it's done and the device restarts.</p>
                            ` :
                            html`
                                <p>Download <code>KoboRoot.tgz</code>, then copy it into the hidden
                                <code>.kobo</code> folder on your Kobo. Safely unplug when it's done and the
                                device restarts.</p>
                            `
                        }
                    </div>
                    <div class="modal-foot">
                        <button class="btn btn-ghost btn-sm" @click="${this.handleClose}">Cancel</button>
                        <button class="btn btn-primary btn-sm" @click="${this.handleDownload}">
                            ${installIcon}
                            ${this.deviceStore.canDirectInstall ? 'Write to Kobo' : 'Download'}
                        </button>
                    </div>
                </div>
            `

        render(template, root)
    }
}
